import dayjs, { Dayjs } from 'dayjs';
import numeral from 'numeral';
import { UtilityType } from 'switch-shared/modules/crud/utility/utility.enums';
import { DurationUnit } from 'switch-shared/utils/duration.enum';

import { BucketSize } from '../enums/utility-data';

export const capitalize = (value: string) => {
  return value.slice(0, 1).toUpperCase().concat(value.slice(1));
};

export const formatNumber = (value: number, decimals?: number) => {
  return Intl.NumberFormat('en-US', {
    style: 'decimal',
    maximumFractionDigits: decimals ?? 3,
    minimumFractionDigits: 0,
  }).format(value);
};

export const roundUsage = (
  num: number | null | undefined,
  decimals: number
) => {
  if (num == null) return null;
  const factorOfTen = Math.pow(10, decimals);
  return Math.round(num * factorOfTen) / factorOfTen;
};

export const roundAndFormatNumber = (num: number, decimals: number) => {
  return formatNumber(roundUsage(num, decimals), decimals);
};

export const formatDateByBucket = (
  date: Date,
  bucketSize?: BucketSize,
  full?: boolean
) => {
  const day = dayjs(date);
  switch (bucketSize) {
    case BucketSize.DAILY:
      return day.format(full ? 'D MMMM YYYY' : 'DD/MM');
    case BucketSize.HOURLY:
      return day.format(full ? 'DD MMM YYYY, HH:mm' : 'HH:mm');
    case BucketSize.HALF_HOURLY:
      return day.format(full ? 'DD MMM YYYY, HH:mm' : 'HH:mm');
    case BucketSize.QUARTER_HOURLY:
      return day.format(full ? 'DD MMM YYYY, HH:mm' : 'HH:mm');
    case BucketSize.MINUTELY:
      return day.format(full ? 'DD MMM YYYY, HH:mm' : 'HH:mm');
    default:
      return day.format('D MMM YYYY, HH:mm:ss');
  }
};

export const formatDateByBucketUnit = (
  date: Date,
  unit?: DurationUnit,
  full?: boolean
) => {
  const day = dayjs(date);
  switch (unit) {
    case DurationUnit.Weeks:
      return day.format(full ? "D MMM 'YY" : 'DD/MM');
    case DurationUnit.Days:
      return day.format(full ? "D MMM 'YY" : 'DD/MM');
    case DurationUnit.Hours:
      return day.format(full ? "DD MMM 'YY, HH:mm" : 'HH:mm');
    case DurationUnit.Minutes:
      return day.format(full ? "DD MMM 'YY, HH:mm" : 'HH:mm');
    default:
      return day.format('D MMM YY, HH:mm:ss');
  }
};

export const getDateFormat = (bucketSize?: BucketSize, full?: boolean) => {
  switch (bucketSize) {
    case BucketSize.DAILY:
      return full ? 'D MMMM YYYY' : 'MMM dd';
    case BucketSize.HOURLY:
    case BucketSize.HALF_HOURLY:
    case BucketSize.QUARTER_HOURLY:
    case BucketSize.MINUTELY:
      return full ? 'dd MMM yyyy, HH:mm' : 'MMM dd HH:mm';
    default:
      return 'd MMM yyyy, HH:mm:ss';
  }
};

export const getDateSliderTitle = (
  dateRange: 'day' | 'month' | 'custom',
  startDate?: Dayjs,
  endDate?: Dayjs
) => {
  switch (dateRange) {
    case 'day':
      return dayjs(startDate).format('D MMM YYYY');
    case 'month':
      return dayjs(startDate).format('MMMM YYYY');
    default:
      return `${dayjs(startDate).format('DD MMM YYYY')} - ${dayjs(
        endDate
      ).format('DD MMM YYYY')}`;
  }
};

export const isNumber = (input: string): boolean => {
  const regex = /^[0-9.]*$/;
  if (regex.test(input) && (input.match(/\./g) || []).length < 2) {
    return true;
  }
  return false;
};

export const graphFileName = (
  title: string,
  startTime: Date | string | Dayjs,
  endTime: Date | string | Dayjs
) => {
  const fileName = `${title.replace(' ', '')}${dayjs(startTime).format('DD-MM-YYYY_HH/mm/ss')}-${dayjs(endTime).format('DD-MM-YYYY_HH/mm/ss')}`;
  return fileName;
};

export const formatCurrency = (value: number, type = 'float') => {
  if (type === 'integer') {
    return numeral(value).format('R0,0');
  }

  return numeral(value).format('R0,0.00');
};

export function formatUtilityUsage(value: number): string {
  const scaledValue = Math.round((value / 1000) * 1000) / 1000;
  return numeral(scaledValue).format('0,0.000');
}

export const getUtilityUnits = (utilityType?: UtilityType, kilo?: boolean) => {
  switch (utilityType) {
    case UtilityType.WATER:
      return `${kilo ? 'k' : ''}L`;
    case UtilityType.ELECTRICITY:
      return `${kilo ? 'k' : ''}Wh`;
    case UtilityType.GAS:
      return `${kilo ? '' : 'c'}m3`;
    default:
      return 'unit';
  }
};

export const formatSendgridEvent = (event: string) => {
  switch (event) {
    case 'group_resubscribe':
      return 'Group Resubscribe';
    case 'group_unsubscribe':
      return 'Group Unsubscribe';
    default:
      return capitalize(event);
  }
};
